// src/components/AddLocationModal.jsx

import React, { useState, useEffect, useCallback } from "react";
import debounce from "lodash.debounce";
import "./addLocationModal.css";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NarrowInputField from "../../components/input/new/narrowInputField";
import NarrowDropdown2 from "../../components/input/new/narrowDropdown2";
import ComboBoxAutoComplete from "../../components/input/comboBoxAutoComplete";

const usaStates = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

const geocodeCache = {};

export default function AddLocationModal({ isOpen, onClose, onAddLocation }) {
  const [locationName, setLocationName] = useState("");
  const [contactName, setContactName] = useState("");
  const [notifEmail, setNotifEmail] = useState("");
  const [notifPhone, setNotifPhone] = useState("");

  const [addressInput, setAddressInput] = useState("");
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [addressDetails, setAddressDetails] = useState({
    city: "",
    state: "",
    zipCode: "",
    latitude: null,
    longitude: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchAutocomplete = useCallback(
    debounce(async (value) => {
      if (value.length < 3) {
        setAutocompleteResults([]);
        return;
      }
      setIsLoading(true);
      setError("");
      try {
        const res = await fetch(
          `https://autocomplete.search.hereapi.com/v1/autocomplete?q=${encodeURIComponent(
            value
          )}&apiKey=${process.env.REACT_APP_HERE_API_KEY}`
        );
        if (!res.ok) throw new Error(res.statusText);
        const { items } = await res.json();
        setAutocompleteResults(items);
      } catch (e) {
        setError("Failed to fetch address suggestions.");
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    fetchAutocomplete(addressInput);
    return () => {
      fetchAutocomplete.cancel();
    };
  }, [addressInput, fetchAutocomplete]);

  const fetchGeocode = async (address) => {
    if (geocodeCache[address]) {
      return geocodeCache[address];
    }
    try {
      const res = await fetch(
        `https://geocode.search.hereapi.com/v1/geocode?q=${encodeURIComponent(
          address
        )}&apiKey=${process.env.REACT_APP_HERE_API_KEY}`
      );
      if (!res.ok) throw new Error(res.statusText);
      const { items } = await res.json();
      if (items.length === 0) return { latitude: null, longitude: null };
      const { lat, lng } = items[0].position;
      const geo = { latitude: lat, longitude: lng };
      geocodeCache[address] = geo;
      return geo;
    } catch {
      setError("Failed to retrieve geographic coordinates.");
      return { latitude: null, longitude: null };
    }
  };

  const handleAddressChange = (value) => {
    setAddressInput(value);
    setSelectedSuggestion(null);
    setAddressDetails({
      city: "",
      state: "",
      zipCode: "",
      latitude: null,
      longitude: null,
    });
  };

  const handleSelectSuggestion = async (suggestion) => {
    if (typeof suggestion === "string") {
      setAddressInput(suggestion);
      setSelectedSuggestion(null);
      setAddressDetails({
        city: "",
        state: "",
        zipCode: "",
        latitude: null,
        longitude: null,
      });
    } else {
      setSelectedSuggestion(suggestion);
      setAddressInput(suggestion.title);
      setAutocompleteResults([]);

      const city = suggestion.address?.city || "";
      const state = suggestion.address?.state || "";
      const zipCode = suggestion.address?.postalCode || "";

      setAddressDetails((d) => ({ ...d, city, state, zipCode }));

      const geo = await fetchGeocode(suggestion.title);
      setAddressDetails((d) => ({
        ...d,
        latitude: geo.latitude,
        longitude: geo.longitude,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate that we have a location name
    if (!locationName || locationName.trim() === '') {
      alert("Please provide a location name.");
      return;
    }
    
    if (
      addressInput &&
      (!addressDetails.city ||
        !addressDetails.state ||
        !addressDetails.zipCode ||
        addressDetails.latitude === null ||
        addressDetails.longitude === null)
    ) {
      alert("Please select a valid address.");
      return;
    }

    const newLocation = {
      name: locationName.trim(),
      address: selectedSuggestion?.title || addressInput,
      city: addressDetails.city,
      state: addressDetails.state,
      zip: addressDetails.zipCode,
      latitude: addressDetails.latitude,
      longitude: addressDetails.longitude,
      contactName,
      notifEmail,
      notifPhone,
    };
    onAddLocation(newLocation);
    resetAll();
    onClose();
  };

  const resetAll = () => {
    setLocationName("");
    setContactName("");
    setNotifEmail("");
    setNotifPhone("");
    setAddressInput("");
    setAutocompleteResults([]);
    setSelectedSuggestion(null);
    setAddressDetails({
      city: "",
      state: "",
      zipCode: "",
      latitude: null,
      longitude: null,
    });
    setError("");
  };

  const handleCancel = () => {
    resetAll();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="location-modal-overlay">
      <div className="location-modal-content">
        <div className="modal-header">
          <h2 className="locations-modal-title">Add in-person location</h2>
          <IconButton onClick={onClose} className="close-modal-btn">
            <CloseIcon />
          </IconButton>
        </div>

        <form className="modal-body" onSubmit={handleSubmit}>
          <NarrowInputField
            name="Location Name"
            title="Location Name"
            placeholder="Location Name"
            isRequired={true}
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            halfWidth={false}
          />

          <ComboBoxAutoComplete
            title="Address"
            placeholder="Start typing address..."
            options={autocompleteResults}
            value={selectedSuggestion}
            inputValue={addressInput}
            onInputChange={handleAddressChange}
            onChange={handleSelectSuggestion}
            getOptionLabel={(item) => item.title}
            isLoading={isLoading}
            error={error}
          />

          <NarrowInputField
            name="City"
            title="City"
            placeholder="—"
            isRequired={true}
            value={addressDetails.city}
            onChange={() => {}}
            halfWidth={false}
            readOnly
          />

          <NarrowDropdown2
            title="State"
            placeholder="—"
            items={usaStates}
            isRequired={true}
            value={addressDetails.state}
            onChange={() => {}}
            multiple={false}
            halfWidth={false}
            disabled
          />

          <NarrowInputField
            name="Zipcode"
            title="Zipcode"
            placeholder="—"
            isRequired={true}
            value={addressDetails.zipCode}
            onChange={() => {}}
            halfWidth={false}
            readOnly
          />

          <NarrowInputField
            name="Contact person"
            title="Name of contact person"
            subLabel="Not visible to public"
            placeholder="John Appleseed"
            isRequired={true}
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            halfWidth={false}
          />

          <NarrowInputField
            name="Contact Email"
            title="Email for appointment notifications"
            subLabel="Not visible to public"
            placeholder="john@quidpro.ai"
            isRequired={true}
            value={notifEmail}
            onChange={(e) => setNotifEmail(e.target.value)}
            halfWidth={false}
          />

          <p className="add-another-email">Add another</p>

          <NarrowInputField
            name="Contact Phone"
            title="Contact phone number"
            placeholder="() -"
            isRequired={true}
            value={notifPhone}
            onChange={(e) => setNotifPhone(e.target.value)}
            halfWidth={false}
          />

          <hr
            className="h-rule"
            style={{ width: "100%", marginTop: "20px", marginBottom: "20px" }}
          />

          <div className="location-modal-actions">
            <button
              type="button"
              className="locations-cancel-button"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button type="submit" className="locations-submit-button">
              Add location
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
